/**
 * Notifications service layer.
 * Abstracts all Firestore operations for notifications.
 * Supports creating, reading, marking as read, dismissing, grouping,
 * and real-time listeners for unread count.
 *
 * Notification types:
 *  - like, comment, follow (social)
 *  - achievement_unlocked (gamification)
 *  - partner_request, partner_checkin_reminder (accountability)
 *  - streak_milestone (streaks)
 *  - goal_completed (goals)
 *  - workout_shared (feed)
 */
import {
  collection,
  doc,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  where,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const NOTIFICATIONS_COLLECTION = 'notifications';

/**
 * All supported notification types.
 */
export const NOTIFICATION_TYPES = {
  LIKE: 'like',
  COMMENT: 'comment',
  FOLLOW: 'follow',
  MENTION: 'mention',
  ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
  PARTNER_REQUEST: 'partner_request',
  PARTNER_CHECKIN_REMINDER: 'partner_checkin_reminder',
  STREAK_MILESTONE: 'streak_milestone',
  GOAL_COMPLETED: 'goal_completed',
  WORKOUT_SHARED: 'workout_shared',
};

/**
 * Serialize a Firestore notification document snapshot into a plain object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object}
 */
const serializeNotification = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    recipientId: data.recipientId || '',
    senderId: data.senderId || '',
    senderName: data.senderName || '',
    senderUsername: data.senderUsername || '',
    senderAvatarUrl: data.senderAvatarUrl || null,
    type: data.type || '',
    postId: data.postId || null,
    read: data.read || false,
    message: data.message || '',
    metadata: data.metadata || null,
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate().toISOString()
        : data.createdAt || new Date().toISOString(),
    _docSnapshot: docSnap,
  };
};

/**
 * Create a notification document.
 * This is fire-and-forget - errors are logged but not thrown.
 * @param {Object} data
 * @param {string} data.recipientId - The user receiving the notification.
 * @param {string} data.senderId - The user who triggered the notification.
 * @param {string} data.senderName - Display name of the sender.
 * @param {string} data.senderUsername - Username of the sender.
 * @param {string|null} data.senderAvatarUrl - Avatar URL of the sender.
 * @param {string} data.type - The notification type (see NOTIFICATION_TYPES).
 * @param {string|null} data.postId - Related post ID (null for non-post notifications).
 * @param {string} data.message - Human-readable notification message.
 * @param {Object|null} [data.metadata] - Extra data (e.g., achievement id, streak count, goal name).
 * @returns {Promise<void>}
 */
export const createNotification = async (data) => {
  try {
    // Don't notify yourself
    if (data.recipientId === data.senderId) return;

    const notifRef = doc(collection(db, NOTIFICATIONS_COLLECTION));
    await setDoc(notifRef, {
      recipientId: data.recipientId,
      senderId: data.senderId,
      senderName: data.senderName,
      senderUsername: data.senderUsername,
      senderAvatarUrl: data.senderAvatarUrl || null,
      type: data.type,
      postId: data.postId || null,
      read: false,
      message: data.message,
      metadata: data.metadata || null,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    // Fire-and-forget: log but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.error('[Create notification]', error);
    }
  }
};

/**
 * Get paginated notifications for a user, ordered by createdAt descending.
 * @param {string} userId - The recipient user ID.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=20] - Max number of notifications.
 * @returns {Promise<{notifications: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getNotifications = async (userId, lastDoc = null, pageLimit = 20) => {
  try {
    const notifsRef = collection(db, NOTIFICATIONS_COLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        notifsRef,
        where('recipientId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        notifsRef,
        where('recipientId', '==', userId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const notifications = querySnapshot.docs.map(serializeNotification);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { notifications, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get notifications', 'Failed to load notifications. Please try again.');
  }
};

/**
 * Mark a single notification as read.
 * @param {string} notificationId
 * @returns {Promise<void>}
 */
export const markAsRead = async (notificationId) => {
  try {
    const notifRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await updateDoc(notifRef, { read: true });
  } catch (error) {
    handleServiceError(error, 'Mark as read', 'Failed to mark notification as read.');
  }
};

/**
 * Mark all unread notifications for a user as read using a batch write.
 * @param {string} userId - The recipient user ID.
 * @returns {Promise<void>}
 */
export const markAllAsRead = async (userId) => {
  try {
    const notifsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(
      notifsRef,
      where('recipientId', '==', userId),
      where('read', '==', false)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return;

    // Firestore batch writes support up to 500 operations
    const batch = writeBatch(db);
    querySnapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, { read: true });
    });

    await batch.commit();
  } catch (error) {
    handleServiceError(error, 'Mark all as read', 'Failed to mark all notifications as read.');
  }
};

/**
 * Dismiss (delete) a single notification.
 * @param {string} notificationId
 * @returns {Promise<void>}
 */
export const dismissNotification = async (notificationId) => {
  try {
    const notifRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await deleteDoc(notifRef);
  } catch (error) {
    handleServiceError(error, 'Dismiss notification', 'Failed to dismiss notification.');
  }
};

/**
 * Subscribe to real-time unread notification count for a user.
 * @param {string} userId - The recipient user ID.
 * @param {function} callback - Called with the unread count number.
 * @returns {function} Unsubscribe function.
 */
export const onUnreadCountChange = (userId, callback) => {
  const notifsRef = collection(db, NOTIFICATIONS_COLLECTION);
  const q = query(
    notifsRef,
    where('recipientId', '==', userId),
    where('read', '==', false)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.size);
    },
    (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Unread count listener]', error);
      }
      callback(0);
    }
  );
};

/**
 * Group similar notifications together.
 * Groups consecutive notifications of the same type + same postId
 * that occurred within a 1-hour window.
 *
 * For example: 3 likes on the same post within 1 hour become a single grouped notification
 * showing "Alice, Bob, and 1 other liked your post."
 *
 * @param {Array<Object>} notifications - Array of serialized notification objects (sorted by createdAt desc).
 * @returns {Array<Object>} Array of grouped notification objects.
 */
export const groupNotifications = (notifications) => {
  if (!notifications || notifications.length === 0) return [];

  const GROUPING_WINDOW_MS = 60 * 60 * 1000; // 1 hour
  const grouped = [];
  let i = 0;

  while (i < notifications.length) {
    const current = notifications[i];
    const group = [current];

    // Only group likes, comments, and workout_shared (types that relate to specific posts)
    const isGroupable =
      (current.type === NOTIFICATION_TYPES.LIKE ||
        current.type === NOTIFICATION_TYPES.COMMENT ||
        current.type === NOTIFICATION_TYPES.WORKOUT_SHARED) &&
      current.postId;

    if (isGroupable) {
      let j = i + 1;
      const currentTime = new Date(current.createdAt).getTime();

      while (j < notifications.length) {
        const next = notifications[j];
        const nextTime = new Date(next.createdAt).getTime();

        if (
          next.type === current.type &&
          next.postId === current.postId &&
          currentTime - nextTime <= GROUPING_WINDOW_MS
        ) {
          group.push(next);
          j++;
        } else {
          break;
        }
      }

      i = j;
    } else {
      i++;
    }

    if (group.length === 1) {
      grouped.push(group[0]);
    } else {
      // Create a grouped notification
      const senders = group.map((n) => ({
        name: n.senderName,
        username: n.senderUsername,
        avatarUrl: n.senderAvatarUrl,
      }));
      const uniqueSenders = [];
      const seenUsernames = new Set();
      for (const sender of senders) {
        if (!seenUsernames.has(sender.username)) {
          seenUsernames.add(sender.username);
          uniqueSenders.push(sender);
        }
      }

      const firstName = uniqueSenders[0]?.name || 'Someone';
      const otherCount = uniqueSenders.length - 1;
      let groupMessage = '';

      if (current.type === NOTIFICATION_TYPES.LIKE) {
        if (otherCount === 0) {
          groupMessage = `${firstName} liked your post.`;
        } else if (otherCount === 1) {
          groupMessage = `${firstName} and ${uniqueSenders[1]?.name || '1 other'} liked your post.`;
        } else {
          groupMessage = `${firstName} and ${otherCount} others liked your post.`;
        }
      } else if (current.type === NOTIFICATION_TYPES.COMMENT) {
        if (otherCount === 0) {
          groupMessage = `${firstName} commented on your post.`;
        } else if (otherCount === 1) {
          groupMessage = `${firstName} and ${uniqueSenders[1]?.name || '1 other'} commented on your post.`;
        } else {
          groupMessage = `${firstName} and ${otherCount} others commented on your post.`;
        }
      } else {
        groupMessage = current.message;
      }

      grouped.push({
        ...current,
        isGrouped: true,
        groupCount: group.length,
        groupSenders: uniqueSenders,
        groupIds: group.map((n) => n.id),
        message: groupMessage,
        read: group.every((n) => n.read),
      });
    }
  }

  return grouped;
};

/**
 * Section notifications by time period: "Today", "This Week", "Earlier".
 * @param {Array<Object>} notifications - Array of notification objects (sorted by createdAt desc).
 * @returns {{ today: Array, thisWeek: Array, earlier: Array }}
 */
export const sectionNotifications = (notifications) => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Start of this week (Sunday)

  const today = [];
  const thisWeek = [];
  const earlier = [];

  for (const notif of notifications) {
    const notifDate = new Date(notif.createdAt);

    if (notifDate >= todayStart) {
      today.push(notif);
    } else if (notifDate >= weekStart) {
      thisWeek.push(notif);
    } else {
      earlier.push(notif);
    }
  }

  return { today, thisWeek, earlier };
};
